import { ReactNode } from 'react';
import { Box, CircularProgress, DialogActions, DialogContent, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CloseOutlined } from '@ant-design/icons';
import { riseIn } from 'components/ui/styleKit';
import { REDUCED_MOTION } from 'components/ui/tokens';
import { CLOSE_LABEL, stepLabel } from '../content';
import FeaturedIcon, { FeaturedTone } from './FeaturedIcon';
import ToneButton, { ToneButtonProps } from './ToneButton';

export type StepAction = ToneButtonProps;

type Props = {
  titleId: string;
  /** 0-based position among the counted steps; absent once the flow is over. */
  progress?: { index: number; total: number };
  icon: ReactNode;
  tone: FeaturedTone;
  /** The icon ripples once, for the finished step. */
  celebrate?: boolean;
  title: string;
  description: string;
  children?: ReactNode;
  primary: StepAction;
  /** Why the primary action is not available yet, just above it; empty once it is. */
  primaryHint?: string;
  /** The one quieter action, on the left: keep the plan, or go back a step. The last step has none. */
  secondary?: StepAction;
  onClose: () => void;
  busy: boolean;
};

const GUTTER_X = { xs: 2.5, sm: 4 };
// Full-screen on a phone, so the system bars must not cover the close button or the footer.
const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
// About the height of a short step, so the dialog does not jump much when the first one arrives.
const LOADING_MIN_HEIGHT = 280;

// Done segments sit full; the current one fills as its step arrives.
function ProgressBar({ index, total }: { index: number; total: number }) {
  return (
    <Stack direction="row" spacing={0.75} role="img" aria-label={stepLabel(index + 1, total)} sx={{ width: 96, mb: 3 }}>
      {Array.from({ length: total }, (_, step) => (
        <Box key={step} sx={{ flex: 1, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: 'divider' }}>
          <Box
            sx={{
              height: '100%',
              bgcolor: 'primary.main',
              transformOrigin: 'left',
              transform: step <= index ? 'scaleX(1)' : 'scaleX(0)',
              ...(step === index && {
                '@keyframes progressFill': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
                animation: 'progressFill .5s .1s cubic-bezier(.2,.7,.3,1) backwards',
                [REDUCED_MOTION]: { animation: 'none' }
              })
            }}
          />
        </Box>
      ))}
    </Stack>
  );
}

function CloseButton({ onClose, disabled }: { onClose: () => void; disabled?: boolean }) {
  return (
    <IconButton
      aria-label={CLOSE_LABEL}
      onClick={onClose}
      disabled={disabled}
      size="small"
      sx={{
        position: 'absolute',
        top: `calc(12px + ${SAFE_TOP})`,
        right: 12,
        zIndex: 1,
        color: 'text.secondary',
        transition: 'transform .2s ease',
        '&:hover': { transform: 'rotate(90deg)' },
        [REDUCED_MOTION]: { '&:hover': { transform: 'none' } }
      }}
    >
      <CloseOutlined />
    </IconButton>
  );
}

/** Held while the dialog finds out which step comes first; the spinner's label names the dialog meanwhile. */
export function DialogLoading({ titleId, title, onClose }: { titleId: string; title: string; onClose: () => void }) {
  return (
    <>
      <CloseButton onClose={onClose} />
      <DialogContent aria-busy sx={{ minHeight: LOADING_MIN_HEIGHT, display: 'grid', placeItems: 'center' }}>
        <CircularProgress id={titleId} aria-label={title} size={28} />
      </DialogContent>
    </>
  );
}

/** One step of the cancellation dialog: progress, icon, title and a line of text, the body, then the actions. */
export default function DialogStep({
  titleId,
  progress,
  icon,
  tone,
  celebrate,
  title,
  description,
  children,
  primary,
  primaryHint,
  secondary,
  onClose,
  busy
}: Props) {
  const phone = useMediaQuery(useTheme().breakpoints.down('sm'));
  return (
    <>
      <CloseButton onClose={onClose} disabled={busy} />
      {/* A step with no body (the confirmation) sits in the middle of a full-screen phone. */}
      <DialogContent
        sx={{
          px: GUTTER_X,
          pt: { xs: `calc(28px + ${SAFE_TOP})`, sm: 3.5 },
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: children ? 'flex-start' : 'center'
        }}
      >
        <Stack alignItems="center" sx={{ textAlign: 'center' }}>
          {progress && <ProgressBar {...progress} />}
          <FeaturedIcon icon={icon} tone={tone} animate burst={celebrate} />
          <Typography id={titleId} variant="h2" sx={{ mt: 2, ...riseIn(1) }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 380, ...riseIn(2) }}>
            {description}
          </Typography>
        </Stack>
        {children && <Box sx={{ mt: 3, ...riseIn(3) }}>{children}</Box>}
        {primaryHint && (
          <Typography role="status" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { sm: 'right' } }}>
            {primaryHint}
          </Typography>
        )}
      </DialogContent>
      {/* Desktop: secondary left, primary right. Phone: both full width, the primary on top. */}
      <DialogActions
        disableSpacing
        sx={{
          px: GUTTER_X,
          pt: 2,
          pb: { xs: `calc(16px + ${SAFE_BOTTOM})`, sm: 2 },
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'stretch',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        {secondary && <ToneButton {...secondary} disabled={secondary.disabled || busy} fullWidth={phone} />}
        <Box sx={{ ml: { sm: 'auto' } }}>
          <ToneButton {...primary} disabled={primary.disabled || (busy && !primary.loading)} fullWidth={phone} />
        </Box>
      </DialogActions>
    </>
  );
}
